import { CheckIcon, ChevronDown, Loader2, X } from 'lucide-react';
import {
    type ComponentProps,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import { Button, buttonVariants } from '@apps/web/components/ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@apps/web/components/ui/command';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@apps/web/components/ui/popover';
import { cn } from '@apps/web/lib/utils';

type Option = {
    readonly value: string;
    readonly label: string;
};

type MultiSelectProps = {
    readonly options: Array<Option>;
    readonly selected: Array<string>;
    readonly onChange: (selected: Array<string>) => void;
    readonly placeholder?: string;
    readonly ariaLabel?: string;
    readonly className?: string;
    // When true, the dropdown panel matches the trigger's width instead of
    // the default full width. Opt-in so existing call sites are unaffected.
    readonly matchTriggerWidth?: boolean;
    readonly disabled?: boolean;
    // When set, the trigger renders as a fixed-size pill displaying this
    // label instead of inline chips of selected items. Use this in dense
    // filter rows where the chip layout would balloon the control.
    readonly compactLabel?: string;
    // Filter controls usually suppress the all-selected count because "all" is
    // equivalent to no filter. Expression builders can opt into showing it.
    readonly compactCountMode?: 'partial' | 'selected';
    readonly popoverSide?: ComponentProps<typeof PopoverContent>['side'];
    readonly portalWithinDialog?: boolean;
    // Optional async search + pagination controls
    readonly searchValue?: string;
    readonly onSearchChange?: (value: string) => void;
    readonly isLoading?: boolean;
    readonly canLoadMore?: boolean;
    readonly onLoadMore?: () => void;
};

export function MultiSelect({
    options,
    selected,
    onChange,
    placeholder = 'Select items...',
    ariaLabel,
    className,
    matchTriggerWidth = false,
    disabled = false,
    compactLabel,
    compactCountMode = 'partial',
    popoverSide = 'bottom',
    portalWithinDialog = false,
    searchValue,
    onSearchChange,
    isLoading = false,
    canLoadMore = false,
    onLoadMore,
}: MultiSelectProps) {
    const [open, setOpen] = useState(false);
    const [showEmpty, setShowEmpty] = useState(false);
    const triggerRef = useRef<HTMLElement | null>(null);
    const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
        null,
    );

    const labelMap = useMemo(() => {
        const map = new Map<string, string>();
        for (const option of options) {
            map.set(option.value, option.label);
        }
        return map;
    }, [options]);

    // Keep selected pills in the same order as the `selected` array,
    // independent of the current options list ordering.
    const selectedOptions = useMemo(
        () =>
            selected.map((value) => ({
                value,
                label: labelMap.get(value) ?? value,
            })),
        [selected, labelMap],
    );

    const handleSelect = (value: string) => {
        if (selected.includes(value)) {
            onChange(selected.filter((item) => item !== value));
        } else {
            onChange([...selected, value]);
        }
    };

    const handleRemove = (value: string) => {
        onChange(selected.filter((item) => item !== value));
    };

    // Delay showing the "No results" state to avoid flicker
    useEffect(() => {
        if (isLoading) {
            setShowEmpty(false);
            return;
        }
        const t = setTimeout(() => setShowEmpty(true), 200);
        return () => clearTimeout(t);
    }, [isLoading, searchValue]);

    useEffect(() => {
        if (!portalWithinDialog) {
            setPortalContainer(null);
            return;
        }
        if (!open) {
            setPortalContainer(null);
            return;
        }
        const dialogContent = triggerRef.current?.closest(
            '[data-slot="dialog-content"]',
        ) as HTMLElement | null;
        setPortalContainer(dialogContent ?? null);
    }, [open, portalWithinDialog]);

    useEffect(() => {
        if (disabled) {
            setOpen(false);
        }
    }, [disabled]);

    // Show a count badge only when the selection deviates from "all" or
    // "none" — a partial selection is the interesting case. "All selected"
    // is conceptually the same as "no filter".
    const showCompactCount =
        selected.length > 0 &&
        (compactCountMode === 'selected' || selected.length < options.length);

    return (
        <Popover
            open={open}
            onOpenChange={(nextOpen) => {
                if (disabled) {
                    setOpen(false);
                    return;
                }
                setOpen(nextOpen);
            }}
        >
            <PopoverTrigger asChild={true}>
                {compactLabel ? (
                    <Button
                        ref={(node) => {
                            triggerRef.current = node;
                        }}
                        variant="outline"
                        role="combobox"
                        aria-label={ariaLabel}
                        aria-expanded={open}
                        className={cn(
                            'hover:bg-background h-9 cursor-default justify-between gap-2 font-normal',
                            className,
                        )}
                        disabled={disabled}
                    >
                        <span className="flex items-center gap-1.5">
                            <span>{compactLabel}</span>
                            {showCompactCount && (
                                <Badge
                                    variant="secondary"
                                    className="h-5 px-1.5 py-0 text-xs"
                                >
                                    {selected.length}
                                </Badge>
                            )}
                        </span>
                        <ChevronDown className="text-muted-foreground h-4 w-4 opacity-60" />
                    </Button>
                ) : (
                    <div
                        ref={(node) => {
                            triggerRef.current = node;
                        }}
                        data-slot="button"
                        role="combobox"
                        aria-label={ariaLabel}
                        tabIndex={disabled ? -1 : 0}
                        aria-expanded={open}
                        aria-disabled={disabled}
                        className={cn(
                            buttonVariants({ variant: 'outline' }),
                            'hover:bg-background h-auto min-h-[2.25rem] w-full cursor-default justify-between',
                            !selected.length && 'text-muted-foreground',
                            disabled &&
                                'pointer-events-none cursor-not-allowed opacity-50',
                            className,
                        )}
                        onClickCapture={(event) => {
                            if (disabled) {
                                event.preventDefault();
                                event.stopPropagation();
                            }
                        }}
                        onKeyDown={(event) => {
                            if (disabled) {
                                return;
                            }
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                setOpen((currentOpen) => !currentOpen);
                            }
                        }}
                    >
                        <div className="flex max-h-[6rem] w-full flex-wrap gap-1 overflow-y-auto py-1">
                            {selected.length > 0 ? (
                                selectedOptions.map((option) => (
                                    <Badge
                                        key={option.value}
                                        variant="secondary"
                                        className="mr-1 cursor-pointer py-1 transition-colors hover:bg-gray-200 dark:hover:bg-gray-700"
                                    >
                                        <span className="min-w-0 truncate">
                                            {option.label}
                                        </span>
                                        <button
                                            type="button"
                                            className="ring-offset-background focus:ring-ring ml-1 shrink-0 rounded-full outline-none focus:ring-2 focus:ring-offset-2"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemove(option.value);
                                            }}
                                        >
                                            <X className="text-muted-foreground hover:text-foreground h-3 w-3" />
                                        </button>
                                    </Badge>
                                ))
                            ) : (
                                <span>{placeholder}</span>
                            )}
                        </div>
                    </div>
                )}
            </PopoverTrigger>
            <PopoverContent
                container={portalContainer ?? undefined}
                className={cn(
                    'p-0',
                    matchTriggerWidth
                        ? 'w-[var(--radix-popover-trigger-width)]'
                        : 'w-full',
                )}
                align="start"
                side={popoverSide}
                sideOffset={4}
            >
                <Command>
                    <CommandInput
                        placeholder="Search..."
                        className="h-9"
                        value={searchValue}
                        onValueChange={onSearchChange}
                    />
                    <CommandList>
                        {showEmpty && !isLoading && (
                            <CommandEmpty>No results found.</CommandEmpty>
                        )}
                        <CommandGroup>
                            {isLoading && options.length > 0 && (
                                <CommandItem
                                    disabled={true}
                                    value={(searchValue || 'loading') as string}
                                >
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Loading
                                </CommandItem>
                            )}
                            {options.map((option) => {
                                const isSelected = selected.includes(
                                    option.value,
                                );
                                return (
                                    <CommandItem
                                        key={option.value}
                                        value={option.label}
                                        onSelect={() =>
                                            handleSelect(option.value)
                                        }
                                    >
                                        <div
                                            className={cn(
                                                'mr-1 flex h-4 w-4 items-center justify-center rounded-sm border',
                                                isSelected
                                                    ? 'border-foreground bg-foreground'
                                                    : 'border-muted-foreground bg-background [&_svg]:invisible',
                                            )}
                                        >
                                            <CheckIcon
                                                className={cn(
                                                    'h-4 w-4',
                                                    isSelected
                                                        ? 'text-background'
                                                        : '',
                                                )}
                                            />
                                        </div>
                                        <span className="text-foreground">
                                            {option.label}
                                        </span>
                                    </CommandItem>
                                );
                            })}
                            {isLoading && options.length === 0 && (
                                <CommandItem
                                    disabled={true}
                                    value={(searchValue || 'loading') as string}
                                >
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Loading…
                                </CommandItem>
                            )}
                            {canLoadMore && !isLoading && (
                                <CommandItem
                                    value="load-more"
                                    onSelect={() => onLoadMore && onLoadMore()}
                                >
                                    Load more…
                                </CommandItem>
                            )}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
